/**
 * Recipe pull rule: a recipe is pulled (shown sold out, unselectable, and
 * refused at generation) when MORE than half of the plants we have a stock
 * signal for are unavailable. Exactly 50% unavailable stays selectable.
 *
 * Shared by the client rollup (useRecipeAvailability) and the server
 * generation gate (checkGenerationStock) so the two can never disagree.
 * Kept dependency-free so both bundles can import it.
 *
 * `overridden` (PLA2-537) is the manual per-tenant staff override: when a
 * retailer knows it can fill a recipe despite low reported stock (the interim
 * guardrail ahead of AWS substitution logic), an override short-circuits the
 * ratio check and the recipe is never pulled. It does NOT affect individual
 * plant availability — unavailable plants are still excluded from the cart at
 * quote time regardless of this flag.
 */
export function isRecipePulled(
  available: number,
  reported: number,
  overridden = false
): boolean {
  if (overridden) return false;
  return reported > 0 && available / reported < 0.5;
}

/**
 * Recipe plants that count as available for the pull rule AFTER swapping
 * (out-of-stock alternatives): a sold-out recipe plant is covered when its
 * substitute is in the design and in stock. Used by the generation gate so it
 * judges the same thing the style picker and alternatives step showed.
 */
export function countCoveredRecipePlants(
  recipePlantIds: string[],
  designedPlantIds: string[],
  isUnavailable: (plantId: string) => boolean,
  substitutions: Record<string, string> = {}
): number {
  const designed = new Set(designedPlantIds);
  return recipePlantIds.filter((id) => {
    if (!isUnavailable(id)) return true;
    const sub = substitutions[id];
    return Boolean(sub && designed.has(sub) && !isUnavailable(sub));
  }).length;
}
